import React from 'react';
import { AlertTriangle } from 'lucide-react';

export interface TaskComplementaryDataPanelProps {
  executionCode: string;
  instanceTitle?: string | null;
  requesterName: string;
  openedAtLabel: string;
  slaLabel: string;
  slaStatus: 'ok' | 'warning' | 'violated' | 'none';
  riskLabel: string;
  riskOk?: boolean;
  priority?: string | null;
}

function MetaRow({
  label,
  value,
  mono,
  valueClassName,
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueClassName?: string;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-baseline gap-2 px-4 py-2.5">
      <p className="truncate font-mono text-[9px] font-bold uppercase tracking-[0.08em] text-slate-400 dark:text-[var(--text-muted)]">
        {label}
      </p>
      <p
        className={`text-right text-[11px] font-semibold leading-snug text-slate-900 dark:text-[var(--text-primary)] ${
          mono ? 'font-mono tracking-[0.04em]' : ''
        } ${valueClassName ?? ''}`}
      >
        {value}
      </p>
    </div>
  );
}

export const TaskComplementaryDataPanel: React.FC<TaskComplementaryDataPanelProps> = ({
  executionCode,
  instanceTitle,
  requesterName,
  openedAtLabel,
  slaLabel,
  slaStatus,
  riskLabel,
  riskOk = true,
  priority,
}) => {
  const slaTone =
    slaStatus === 'violated'
      ? 'text-rose-600 dark:text-rose-400'
      : slaStatus === 'warning'
        ? 'text-amber-600 dark:text-amber-400'
        : slaStatus === 'ok'
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-slate-600 dark:text-[var(--text-secondary)]';

  const riskTone = riskOk
    ? 'text-slate-700 dark:text-[var(--text-secondary)]'
    : 'text-rose-600 dark:text-rose-400';

  const showSlaAlert = slaStatus === 'violated' || slaStatus === 'warning';

  return (
    <div className="border-b border-slate-100 dark:border-[var(--border-default)]">
      {showSlaAlert && (
        <div
          className={`mx-3 mb-2 mt-3 flex items-start gap-2 rounded-sm border px-3 py-2 ${
            slaStatus === 'violated'
              ? 'border-rose-200 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/20'
              : 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20'
          }`}
        >
          <AlertTriangle
            size={12}
            className={`mt-0.5 shrink-0 ${slaStatus === 'violated' ? 'text-rose-500' : 'text-amber-500'}`}
          />
          <div className="min-w-0">
            <p
              className={`font-mono text-[9px] font-bold uppercase tracking-[0.08em] ${
                slaStatus === 'violated' ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300'
              }`}
            >
              {slaStatus === 'violated' ? 'SLA violado' : 'SLA em risco'}
            </p>
            <p className={`mt-0.5 font-mono text-[10px] tabular-nums ${slaTone}`}>
              {slaStatus === 'violated' ? `−${slaLabel}` : slaLabel}
            </p>
          </div>
        </div>
      )}

      <div className="divide-y divide-slate-100 dark:divide-[var(--border-default)]">
        <MetaRow label="ID da Ação" value={executionCode} mono />
        {instanceTitle && <MetaRow label="Demanda" value={instanceTitle} />}
        <MetaRow label="Solicitante" value={requesterName} />
        <MetaRow label="Abertura" value={openedAtLabel} />
        <MetaRow label="SLA" value={slaStatus === 'violated' ? `−${slaLabel}` : slaLabel} valueClassName={slaTone} />
        <MetaRow label="Risco" value={riskLabel} valueClassName={riskTone} />
        {priority && (
          <MetaRow
            label="Prioridade"
            value={priority.charAt(0).toUpperCase() + priority.slice(1)}
          />
        )}
      </div>
    </div>
  );
};

export default TaskComplementaryDataPanel;
